import { PostAsync } from './api';

const TranslationEndpoint = (targetLanguage: string) =>
  `translations/${targetLanguage}`;

const BATCH_SIZE = 120;

export interface TranslationProgressEvent {
  status: 'processing' | 'completed' | 'error';
  current: number;
  total: number;
  message: string;
}

/**
 * Translate a list of text using the OpenAI.
 * Splits into batches to avoid 504 gateway timeout.
 * @param targetLanguage The language to translate to.
 * @param data The data to be translated.
 * @param onProgress Optional callback for progress updates.
 * @returns The translated version on the original text based on the target language.
 * @see https://developer.mozilla.org/en-US/docs/Web/HTTP/Methods/POST
 */
export const TranslateUsingOpenAIAsync = async (
  targetLanguage: string,
  data: Record<string, string>,
  onProgress?: (event: TranslationProgressEvent) => void
): Promise<Record<string, string>> => {
  const entries = Object.entries(data);
  const totalBatches = Math.ceil(entries.length / BATCH_SIZE);

  // If entries are empty, return immediately
  if (entries.length === 0) return {};

  // If only 1 batch, no need to split
  if (totalBatches === 1) {
    onProgress?.({
      status: 'processing',
      current: 1,
      total: 1,
      message: `Translating ${entries.length} resources...`
    });

    const result = await PostAsync<Record<string, string>>(
      TranslationEndpoint(targetLanguage),
      data
    );

    onProgress?.({
      status: 'completed',
      current: 1,
      total: 1,
      message: 'Translation completed!'
    });

    return result;
  }

  // Split into batches and send sequentially
  const results: Record<string, string> = {};

  for (let i = 0; i < totalBatches; i++) {
    const batchEntries = entries.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE);
    const batch = Object.fromEntries(batchEntries);

    onProgress?.({
      status: 'processing',
      current: i + 1,
      total: totalBatches,
      message: `Translating batch ${i + 1} of ${totalBatches} (${batchEntries.length} resources)...`
    });

    const translated = await PostAsync<Record<string, string>>(
      TranslationEndpoint(targetLanguage),
      batch
    );

    Object.assign(results, translated);
  }

  onProgress?.({
    status: 'completed',
    current: totalBatches,
    total: totalBatches,
    message: 'Translation completed!'
  });

  return results;
};
